import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

import type { WebViewBootstrapResultDTO } from '@nora-monorepo/nora-api-client';
import { OperatingSystem } from '@nora-monorepo/nora-api-client';

import {
  clearPersistedStores,
  getPersistedStoreKeys,
  persistedStoreStorage,
} from '#ui-shared/pinia/persist/local-storage-pinia-storage';
import type {
  PiniaPersistFailedEvent,
  PiniaStorageUpdateEvent,
} from '#ui-shared/pinia/persist/persisted-store.types';
import { useDeviceStore } from '#ui-shared/stores/device-store';
import { useGenericAppStore } from '#ui-shared/stores/generic-app-store';
import { useToastStore } from '#ui-shared/stores/toast-store';
import {
  isWebViewSimulatorChild,
  postEventToSimulatorParent,
  setupSimulatorParentBridge,
} from '#ui-shared/stores/webview/webview-simulator-bridge';
import type {
  NoraWebViewBootstrapCleanEvent,
  NoraWebViewConnectivityChangedEvent,
  NoraWebViewEvent,
  NoraWebViewResponse,
  NoraWebViewResponseEvent,
} from '#ui-shared/stores/webview/webview.types';
import {
  NoraWebViewEventPattern,
} from '#ui-shared/stores/webview/webview.types';
import type { JSONValue } from '#ui-shared/types/json';
import { Base64Utils } from '#ui-shared/utils/base64';
import { appLogger } from '#ui-shared/utils/logger/LoggerClass';

/** Convenience type alias */
type WebViewUrl = string;

const RESPONSE_EVENT_TIMEOUT = 30000;

/**
 * Sent to the native layer in place of the state itself, which now lives in the webview's own
 * storage. The native layer only has to know some exists, and a marker keeps the bridge from
 * carrying a multi-megabyte map snapshot on every change.
 */
const PERSISTED_STATE_MARKER = '1';

/**
 * Where the last clean bootstrap's responses are kept, so an offline start has something to answer
 * `settings/public` with instead of hanging on it.
 */
export const BOOTSTRAP_SNAPSHOT_STORAGE_KEY = 'webview.bootstrapSnapshot';

/**
 * Storage form of a cached bootstrap response. `data` is kept decoded, unlike the
 * {@link NoraWebViewResponse} the cache serves: base64 costs about a third more characters than
 * the JSON it wraps, and this snapshot shares its quota with the persisted stores.
 */
interface StoredBootstrapResponse extends Omit<NoraWebViewResponse, 'data'> {
  data: JSONValue;
}

/** Reads back the responses stored by the last clean bootstrap, re-encoding them as they load. */
function readBootstrapSnapshot(): Record<WebViewUrl, NoraWebViewResponse> | null {
  try {
    const raw = localStorage.getItem(BOOTSTRAP_SNAPSHOT_STORAGE_KEY);

    if (raw === null || raw.length === 0) {
      return null;
    }

    const parsed = JSON.parse(raw) as Record<WebViewUrl, StoredBootstrapResponse>;
    const responses: Record<WebViewUrl, NoraWebViewResponse> = {};

    for (const [url, stored] of Object.entries(parsed)) {
      // Earlier builds stored `data` already encoded. Re-encoding that yields a base64 string
      // wrapping a base64 string, which fails further down, so drop the snapshot and refetch.
      if (typeof stored.data === 'string') {
        console.warn('[WebViewStore] Discarding a bootstrap snapshot in the previous format');

        return null;
      }

      responses[url] = { ...stored, data: Base64Utils.toBase64(stored.data) };
    }

    return Object.keys(responses).length > 0 ? responses : null;
  }
  catch (error) {
    console.warn('[WebViewStore] Could not read the stored bootstrap snapshot', error);

    return null;
  }
}

/**
 * Stores the current bootstrap responses for the next offline start. Written straight to
 * localStorage so it emits no persisted-state marker: this is server data, not a user change.
 */
function saveBootstrapSnapshot(responses: Record<WebViewUrl, StoredBootstrapResponse>): void {
  try {
    // Removed first so a failed write cannot leave the previous snapshot behind, which would serve
    // the last bootstrap's data on the next offline start.
    localStorage.removeItem(BOOTSTRAP_SNAPSHOT_STORAGE_KEY);
    localStorage.setItem(BOOTSTRAP_SNAPSHOT_STORAGE_KEY, JSON.stringify(responses));
  }
  catch (error) {
    // Over quota: an offline start falls back to reaching the native layer, as it did before this
    // snapshot existed.
    console.warn('[WebViewStore] Could not store the bootstrap snapshot', error);
  }
}

/** Every storage key a persisted bootstrap restores from, so listing and clearing agree. */
export function getWebviewOfflineStorageKeys(): string[] {
  return [...getPersistedStoreKeys(), BOOTSTRAP_SNAPSHOT_STORAGE_KEY];
}

/** Removes everything a persisted bootstrap would restore from. */
export function clearWebviewOfflineStorage(): void {
  for (const key of getWebviewOfflineStorageKeys()) {
    localStorage.removeItem(key);
  }
}

export const useWebViewStore = defineStore('webViewStore', () => {
  const isWebView = ref(false);
  const bootstrapType = ref<'clean' | 'persisted' | null>(null);

  /**
   * Whether the bootstrap window is still open (between `bootstrap.clean` and
   * {@link bootstrapComplete}). While open, requests are served from {@link webviewResponseCache}
   * only and a miss returns null immediately, so bootstrap can't hang on a request the native
   * layer can't answer. After it closes, {@link fetchWebviewResponse} goes by connectivity instead.
   */
  const bootstrapWindowActive = ref(false);
  /** Represents the bootstrap data cache. It consists of key/value pairs, where the key is the 'url' from which the data was retrieved, and the value is the response data. */
  const webviewResponseCache: Map<WebViewUrl, NoraWebViewResponse> = new Map();
  const deviceStore = useDeviceStore();
  const appStore = useGenericAppStore();

  // On reconnect, drop the snapshot so reads go live again; offline we keep it as the read source.
  // Not during the bootstrap window though - the required reads (groups, operations, user) only
  // live in the snapshot, so clearing then would break them; bootstrapComplete handles that case.
  watch(
    () => appStore.isOnline,
    (isOnline) => {
      if (isOnline && !bootstrapWindowActive.value) {
        webviewResponseCache.clear();
      }
    },
  );

  const pendingResponses = new Map<string, (response: NoraWebViewResponse) => void>();

  persistedStoreStorage.addEventListener('storage-update', (event: Event) => {
    const customEvent = event as PiniaStorageUpdateEvent;

    sendEventToNativeLayer(
      NoraWebViewEventPattern.UPDATE_PERSISTED_STATE,
      { [customEvent.detail.key]: PERSISTED_STATE_MARKER },
    );
  });

  // Storage ran out even after the snapshot was given up for it. Told to the user, not just the
  // console: the marker from this store's last successful write still stands, so the next offline
  // start comes back to that older state and everything done since is gone.
  persistedStoreStorage.addEventListener('persist-failed', (event: Event) => {
    const customEvent = event as PiniaPersistFailedEvent;

    appLogger.error({
      message: 'Could not persist offline state, storage is full',
      sensitiveContext: { key: customEvent.detail.key },
    });

    useToastStore().addError({
      message: 'Offline changes are not being saved',
      detail: 'This device has run out of storage space.',
    });
  });

  /**
   * Handler for a `bootstrap.clean` event from the Native Layer.
   *
   * Native layer sends a `bootstrap.clean` event to signal that we should bootstrap with data directly from the API.
   * The event itself contains the bootstrap data, which the Native Layer has fetched.
   * @param event
   */
  function handleCleanBootstrap(event: CustomEvent<NoraWebViewBootstrapCleanEvent>): void {
    appLogger.debug({
      message: 'Received bootstrap clean event',
    });

    bootstrapType.value = 'clean';
    bootstrapWindowActive.value = true;

    // A clean bootstrap means our offline changes are synced, so local state is stale - keeping it
    // could bring back something the user deleted. Runs before any store is created, so it wins.
    clearPersistedStores();

    try {
      const bootstrapData: WebViewBootstrapResultDTO = typeof event.detail.data === 'string'
        ? JSON.parse(Base64Utils.fromBase64(event.detail.data)) as WebViewBootstrapResultDTO
        : event.detail.data;

      const snapshot: Record<WebViewUrl, StoredBootstrapResponse> = {};

      for (const [url, response] of Object.entries(bootstrapData)) {
        // This has to be wrapped in a 'fake' WebViewResponse wrapper to be compatible with the
        // latest changes in which headers can be added, and the data is sent as base64 strings instead to support binary data
        const stored: StoredBootstrapResponse = {
          // The DTO types this as `object`, but it is what a JSON payload parsed into.
          data: { data: response as JSONValue },
          url: '',
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        };

        snapshot[url] = stored;
        webviewResponseCache.set(url, { ...stored, data: Base64Utils.toBase64(stored.data) });
      }

      saveBootstrapSnapshot(snapshot);
    }
    catch (error) {
      appLogger.error({
        message: 'Failed to process bootstrap clean data',
        sensitiveContext: {
          error: {
            message: error instanceof Error ? error.message : 'Unknown error',
            name: error instanceof Error ? error.name : 'Unknown error',
            stack: error instanceof Error ? error.stack : 'No stack trace',
          },
        },
      });

      bootstrapWindowActive.value = false;
    }
  }

  /**
   * Handler for a `bootstrap.persisted` event from the Native Layer.
   *
   * Native layer sends a `bootstrap.persisted` event to signal that we should bootstrap with offline data.
   * This is relevant when the device has no internet connection, or limited internet connection.
   *
   * The event's payload is unused: stores restore themselves as they are created, and the bootstrap
   * responses come from the snapshot the last clean bootstrap stored.
   */
  function handlePersistedBootstrap(): void {
    appLogger.debug({
      message: 'Received bootstrap persisted event',
    });

    bootstrapType.value = 'persisted';

    const snapshot = readBootstrapSnapshot();

    if (!snapshot) {
      // The native layer thinks we have local state but our storage is gone. Leaving the window
      // closed lets requests reach the native layer; an empty one retries settings/public forever.
      appLogger.error({
        message: 'Received a persisted bootstrap without a stored bootstrap snapshot',
      });

      return;
    }

    for (const [url, response] of Object.entries(snapshot)) {
      webviewResponseCache.set(url, response);
    }

    bootstrapWindowActive.value = true;
  }

  // The native layer owns the network in webview, so 'native' makes it authoritative from here on
  // (browser online/offline events are ignored afterwards). Connectivity drives request handling
  // once the bootstrap window closes, see fetchWebviewResponse.
  function handleConnectivityChanged(event: CustomEvent<NoraWebViewConnectivityChangedEvent>): void {
    appStore.setNetworkOnline(event.detail.data.online, 'native');
  }

  /**
   * Initializes the Native Layer event listeners.
   *
   * Without this, we won't be listening for bootstrap events.
   */
  async function nativeLayerInit(): Promise<void> {
    addNativeLayerEventListener(
      NoraWebViewEventPattern.CONNECTIVITY_CHANGED,
      (event: CustomEvent<NoraWebViewConnectivityChangedEvent>) => {
        handleConnectivityChanged(event);
      },
    );

    return new Promise<void>((resolve) => {
      addNativeLayerEventListener(
        NoraWebViewEventPattern.BOOTSTRAP_WEBVIEW_CLEAN,
        (event: CustomEvent<NoraWebViewBootstrapCleanEvent>) => {
          handleCleanBootstrap(event);
          resolve();
        },
      );

      addNativeLayerEventListener(
        NoraWebViewEventPattern.BOOTSTRAP_WEBVIEW_PERSISTED,
        () => {
          handlePersistedBootstrap();
          resolve();
        },
      );
    });
  }

  function sendRequestToNativeLayer(
    method: string,
    url: string,
    data: unknown,
  ): void {
    sendEventToNativeLayer(NoraWebViewEventPattern.REQUEST, {
      method,
      url,
      data,
    });
  }

  function addNativeLayerEventListener<TEvent extends Event = Event>(
    pattern: string,
    eventCallback: (event: TEvent) => void,
  ): () => void {
    const wrappedEventCallback = (event: Event): void => {
      eventCallback(event as TEvent);
    };

    window.addEventListener(pattern, wrappedEventCallback);

    const removeHandler = (): void => {
      window.removeEventListener(pattern, wrappedEventCallback);
    };

    return removeHandler;
  }

  async function initiateWebView(): Promise<void> {
    isWebView.value = true;

    if (isWebViewSimulatorChild()) {
      // Inside the simulator iframe: re-dispatch the parent's messages as the window CustomEvents
      // the native-layer listeners already expect.
      setupSimulatorParentBridge(
        (pattern, data) => simulateNativeLayerEvent(pattern as NoraWebViewEventPattern, data),
      );
    }

    sendEventToNativeLayer(NoraWebViewEventPattern.WEBVIEW_INITIALIZED);

    await nativeLayerInit();
    setupResponseListener();
  }

  async function setupResponseListener(): Promise<void> {
    addNativeLayerEventListener(
      NoraWebViewEventPattern.RESPONSE,
      (event: CustomEvent<NoraWebViewResponseEvent>) => {
        const response = event.detail.data;
        const url = response.url;

        console.debug(`Received response for URL: ${url}, with response: ${JSON.stringify(response)}`);

        if (pendingResponses.has(url)) {
          pendingResponses.get(url)?.(response);

          pendingResponses.delete(url);
        }
        else {
          console.warn(`No pending response listener for URL: ${url}`);
        }
      },
    );
  }

  function simulateNativeLayerEvent(
    pattern: NoraWebViewEventPattern,
    data: unknown,
  ): void {
    console.debug('[WebViewStore] Simulating Native Layer event:', {
      pattern,
      data,
      timestamp: new Date().toISOString(),
    });

    const event = new CustomEvent(pattern, {
      detail: {
        data,
      },
    });

    window.dispatchEvent(event);
  }

  /**
   * Sends the given event pattern to the Native Layer.
   *
   * The Native Layer implements handlers to interact with the Webview. These are accessible on the Javascript/Webview side through the `window` object.
   *
   * For Android, the handler is `webview.noraHandler`. For iOS, it is `window.webkit.messageHandler.noraHandler`. Both handlers have a `postMessage` function, which we can use to send messages to the Native Layer.
   *
   * We store the current operating system in the `deviceStore`.
   * @param pattern
   * @param data
   */
  function sendEventToNativeLayer(
    pattern: NoraWebViewEventPattern,
    data?: Record<string, unknown>,
  ): void {
    if (!isWebView.value) {
      return;
    }

    console.debug('[WebViewStore] Sending event to native layer: ', JSON.stringify({
      pattern,
      timestamp: new Date().toISOString(),
    }));

    const operatingSystem = deviceStore.operatingSystem;

    const event: NoraWebViewEvent = {
      pattern,
      data,
    };

    if (isWebViewSimulatorChild()) {
      // The simulator (parent window) acts as the Native Layer.
      postEventToSimulatorParent(event);

      return;
    }

    if (operatingSystem === OperatingSystem.WEB) {
      // Web can not run in webview
    }
    else if (operatingSystem === OperatingSystem.ANDROID) {
      if (!window.noraHandler) {
        return;
      }

      // Android requires a string to be sent
      return window.noraHandler.postMessage(JSON.stringify(event));
    }
    else if (operatingSystem === OperatingSystem.IOS) {
      if (!window.webkit) {
        return;
      }

      return window.webkit.messageHandlers.noraHandler.postMessage(event);
    }
  }

  /**
   * Fetches a webview response from the provided `url`, given the provided `method` and `data`.
   *
   * It will first attempt to retrieve a cached response to the provided url, stored in the local offline cache. See {@link webviewResponseCache}.
   *
   * During the bootstrap window, or while offline, a cache miss returns null instead of reaching the native layer (which could not answer). Online and after the window, the request is forwarded to the native layer.
   * @param method - the method is irrelevant when getting from local cache, but will be useful when forwarding the request to the native layer
   * @param url - the url of the request
   * @param data - data to be sent to the Native layer
   * @returns `null`  if no locally cached response exists and we cannot reach the native layer (bootstrap window or offline)
   * @returns `NoraWebViewResponse`  for the provided URL, either locally cached or by routing an API request through the Native Layer
   */
  async function fetchWebviewResponse(method: string, url: URL, data: unknown): Promise<NoraWebViewResponse | null> {
    const urlString = `${url.pathname}${url.search}`;
    console.debug(`Getting offline response for URL: ${urlString}`);
    const cachedResponse = webviewResponseCache.get(urlString);

    // During the bootstrap window, serve only from the snapshot; a miss returns null so bootstrap
    // can't hang on a request that can't be answered.
    if (bootstrapWindowActive.value) {
      return cachedResponse ?? null;
    }

    // Offline, going to the native layer for live data would hang until timeout, so serve the
    // retained snapshot and fast-fail on a miss.
    if (!appStore.isOnline) {
      return cachedResponse ?? null;
    }

    return await waitForResponseEvent(method, urlString, data);
  }

  function bootstrapComplete(): void {
    bootstrapWindowActive.value = false;

    // Online: drop the snapshot so reads go live. Offline: keep it; the reconnect watcher clears
    // it once we're back online.
    if (appStore.isOnline) {
      webviewResponseCache.clear();
    }

    sendEventToNativeLayer(NoraWebViewEventPattern.WEBVIEW_BOOTSTRAP_COMPLETED);
  }

  /**
   * Forwards a request to the Native Layer, and adds the request to a list of pending responses.
   *
   * Will resolve to the response, or `null` if no response.
   * @param method
   * @param url
   * @param data
   */
  function waitForResponseEvent(
    method: string,
    url: string,
    data: unknown,
  ): Promise<NoraWebViewResponse | null> {
    sendRequestToNativeLayer(method, url, data);

    return new Promise((resolve) => {
      pendingResponses.set(url, resolve);

      setTimeout(() => {
        if (pendingResponses.has(url)) {
          console.warn(`No response received for URL: ${url}`);

          pendingResponses.delete(url);

          resolve(null);
        }
      }, RESPONSE_EVENT_TIMEOUT);
    });
  }

  return {
    isWebView,
    bootstrapType,
    bootstrapWindowActive,

    addNativeLayerEventListener,
    initiateWebView,
    sendRequestToNativeLayer,
    sendEventToNativeLayer,
    fetchWebviewResponse,
    bootstrapComplete,
  };
});
